// ============================================
// Auth Context - Supabase Session Management
// ============================================

import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import { clearCache, clearCloudQueryCache } from '../data/hooks';
import { clearSemanticSearchCache } from '../data/useSemanticSearch';
import { loginToRevenueCat, logoutFromRevenueCat } from '../utils/revenuecat';
import { maybeSendWelcomeEmail } from '../data/cloud';
import { trackEvent } from '../utils/analytics';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

// signup_completed telemetry. An auth.users row younger than this window at
// SIGNED_IN time is a just-created account; returning sign-ins carry an old
// created_at and never fire. Mirrors SignUpPage's returning-account defense
// (SIGNUP_MAX_NEW_ACCOUNT_AGE_MS = 35 min).
const SIGNUP_NEW_ACCOUNT_WINDOW_MS = 35 * 60 * 1000;
const SIGNUP_TRACKED_KEY = 'prtcl_signup_completed_tracked';

// Per-user-id dedupe so signup_completed fires at most once per account even
// if SIGNED_IN fires again inside the window (sign out + back in, token
// churn). Returns true the first time an id is seen. Best-effort: if storage
// is unavailable we fire (better one duplicate than a silent miss).
function claimSignupCompleted(userId: string): boolean {
  try {
    const raw = localStorage.getItem(SIGNUP_TRACKED_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(ids) ? (ids as string[]) : [];
    if (list.includes(userId)) return false;
    list.push(userId);
    localStorage.setItem(SIGNUP_TRACKED_KEY, JSON.stringify(list.slice(-20)));
    return true;
  } catch {
    return true;
  }
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Track the last-seen user id so we can detect sign-out AND user swaps
  // (e.g. User A signs out then User B signs in on the same device). In
  // both cases the useCloudQuery cache must be purged so User B can't
  // briefly see User A's cached rows before their own fetch resolves.
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Get initial session. Catch path matters: if getSession rejects
    // (corrupted token storage, iOS WebView storage glitch on cold start
    // after an OS update), `loading` would stick at true forever and the
    // app would hang on the splash screen. Capture to Sentry, force the
    // loading flag false so routes can render their unauthenticated state.
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        if (session?.user) {
          lastUserIdRef.current = session.user.id;
          Sentry.setUser({ id: session.user.id });
          void loginToRevenueCat(session.user.id);
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
      })
      .finally(() => {
        setLoading(false);
      });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUserId = session?.user?.id ?? null;
      const prevUserId = lastUserIdRef.current;
      // Clear the cloud query cache when the user actually changes, not on
      // every token refresh (TOKEN_REFRESHED fires every hour in the
      // background and keeps the same user id). Covers SIGNED_OUT,
      // SIGNED_IN with a different user, and any unexpected user swap.
      if (event === 'SIGNED_OUT' || prevUserId !== nextUserId) {
        clearCloudQueryCache();
        // Library search results can carry premium KB excerpts - never let
        // them survive into another user's session.
        clearSemanticSearchCache();
      }
      lastUserIdRef.current = nextUserId;

      setSession(session);
      setLoading(false);
      if (session?.user) {
        Sentry.setUser({ id: session.user.id });
        void loginToRevenueCat(session.user.id);
        // Fire the new-account welcome email. Gated to SIGNED_IN (the actual
        // sign-in action, all providers) so returning users restoring a stored
        // session on cold start (INITIAL_SESSION) don't trigger it. Server-side
        // idempotency + the existing-user backfill make double-fires and
        // returning sign-ins safe no-ops. Fire-and-forget, never blocks auth.
        if (event === 'SIGNED_IN') {
          maybeSendWelcomeEmail(session.user.id);
          // signup_completed: represents ACCOUNT CREATION, not every sign-in.
          // Gate to accounts created inside the new-account window (returning
          // sign-ins have an old created_at) and dedupe per user id. Covers
          // every provider (email OTP, Apple, Google) at this single choke
          // point. Fire-and-forget, never blocks auth.
          const createdMs = session.user.created_at ? new Date(session.user.created_at).getTime() : 0;
          const isNewAccount = createdMs > 0 && Date.now() - createdMs < SIGNUP_NEW_ACCOUNT_WINDOW_MS;
          if (isNewAccount && claimSignupCompleted(session.user.id)) {
            void trackEvent('signup_completed', { provider: session.user.app_metadata?.provider ?? 'unknown' });
          }
        }
        // Returning user signed in - mark welcome as seen so native app
        // doesn't show onboarding again on next open.
        try { localStorage.setItem('prtcl_welcome_seen_v2', 'true'); }
        catch { /* localStorage unavailable - non-fatal */ }
      } else {
        Sentry.setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    clearCache();
    await logoutFromRevenueCat();
    await supabase.auth.signOut();
  }, []);

  // Memoize context value so consumers don't re-render on unrelated Layout
  // re-renders. Identity changes only when session, loading, or signOut
  // (stable) change - which is exactly when consumers legitimately need
  // to re-render.
  const contextValue = useMemo(
    () => ({ session, user: session?.user ?? null, loading, signOut }),
    [session, loading, signOut],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
